import { useMemo, useState } from "react";
import { Check, Snowflake } from "lucide-react";
import { hamKaasBatchRest } from "@/lib/hamKaasVoorraad";

const today = () => new Date().toISOString().slice(0, 10);
const number = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function HamKaasInvriesVoorraad({
  batches,
  entries,
  helpers,
  hulpstofInkoop,
  canEdit,
  inp,
  kgf,
  onAdd,
  onAdoptCount,
}) {
  const [form, setForm] = useState({
    datum: today(),
    naam: "",
    kg: "",
    tracecode: "",
    bronTracecode: "",
    locatie: "Vriezer",
    opmerking: "",
  });
  const [counts, setCounts] = useState({});
  const hamKaas = (helpers || []).filter((helper) => helper.type === "Ham/kaas");
  const stats = useMemo(() => (
    (batches || [])
      .map((batch) => ({ ...batch, rest: hamKaasBatchRest(batch, entries) }))
      .sort((left, right) => String(right.datum || "").localeCompare(String(left.datum || "")))
  ), [batches, entries]);
  const sourceOptions = (hulpstofInkoop || [])
    .filter((batch) => batch?.hulpstof === form.naam && batch.batchnr)
    .sort((left, right) => String(left.datum || "").localeCompare(String(right.datum || "")));
  const defaultTrace = `HK-${String(form.datum || "").replace(/-/g, "")}`;
  const add = () => {
    const tracecode = String(form.tracecode || defaultTrace).trim();
    onAdd?.({ ...form, tracecode });
    setForm({
      datum: form.datum || today(),
      naam: "",
      kg: "",
      tracecode: "",
      bronTracecode: "",
      locatie: "Vriezer",
      opmerking: "",
    });
  };

  return (
    <details
      className="mb-6 rounded-lg border border-cyan-200 bg-cyan-50/60 p-4"
      data-testid="hk-invries-stock"
    >
      <summary
        className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3"
        data-testid="hk-invries-toggle"
      >
        <span className="flex items-center gap-2 text-lg font-bold text-slate-900">
            <Snowflake className="h-5 w-5 text-cyan-700" />
            Ham/kaas — gesneden &amp; ingevroren
        </span>
        <span className="text-sm font-medium text-cyan-800">
          {stats.length} invriespartij{stats.length === 1 ? "" : "en"} · openklappen
        </span>
      </summary>
      <div className="mt-3">
        <p className="mb-3 max-w-4xl text-sm text-slate-600">
            Deze batchkaart staat bij grondstoffen voor tellen en corrigeren, maar telt niet
            opnieuw mee in de totaalvoorraad. Zo ontstaat geen dubbeltelling met de bestaande
            ham/kaas-hulpstofvoorraad. Productie-invoer boekt deze invriespartijen FIFO af.
        </p>
      {canEdit && (
        <div className="grid grid-cols-2 gap-3 rounded-lg border border-cyan-100 bg-white p-3 md:grid-cols-4">
          <label>
            <span className="mb-1 block text-xs font-semibold text-slate-600">Snij-/invriesdatum</span>
            <input
              type="date"
              data-testid="hk-invries-datum"
              className={inp}
              value={form.datum}
              onChange={(event) => setForm({ ...form, datum: event.target.value })}
            />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold text-slate-600">Ham/kaas</span>
            <select
              data-testid="hk-invries-naam"
              className={inp}
              value={form.naam}
              onChange={(event) => setForm({ ...form, naam: event.target.value, bronTracecode: "" })}
            >
              <option value="">— kies artikel —</option>
              {hamKaas.map((helper) => (
                <option key={helper.id || helper.naam} value={helper.naam}>
                  {helper.naam}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold text-slate-600">Ingevroren kg</span>
            <input
              type="number"
              step="any"
              data-testid="hk-invries-kg"
              className={inp}
              value={form.kg}
              onChange={(event) => setForm({ ...form, kg: event.target.value })}
            />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold text-slate-600">Nieuwe dagtracecode</span>
            <input
              data-testid="hk-invries-trace"
              className={inp}
              value={form.tracecode}
              onChange={(event) => setForm({ ...form, tracecode: event.target.value })}
              placeholder={defaultTrace}
            />
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold text-slate-600">Bronpartij</span>
            <select
              data-testid="hk-invries-bron"
              className={inp}
              value={form.bronTracecode}
              onChange={(event) => setForm({ ...form, bronTracecode: event.target.value })}
            >
              <option value="">— brontracecode —</option>
              {sourceOptions.map((batch) => (
                <option key={batch.id} value={batch.batchnr}>
                  {batch.batchnr} · {batch.datum}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="mb-1 block text-xs font-semibold text-slate-600">Locatie</span>
            <input
              data-testid="hk-invries-locatie"
              className={inp}
              value={form.locatie}
              onChange={(event) => setForm({ ...form, locatie: event.target.value })}
            />
          </label>
          <label className="col-span-2">
            <span className="mb-1 block text-xs font-semibold text-slate-600">Opmerking</span>
            <input
              data-testid="hk-invries-opmerking"
              className={inp}
              value={form.opmerking}
              onChange={(event) => setForm({ ...form, opmerking: event.target.value })}
              placeholder="Bijv. na snijden ingevroren"
            />
          </label>
          <div className="col-span-2 flex items-end">
            <button
              data-testid="hk-invries-add"
              onClick={add}
              className="rounded-md bg-cyan-700 px-4 py-2 text-sm font-semibold text-white
                transition-colors hover:bg-cyan-800"
            >
              Invriespartij registreren
            </button>
          </div>
        </div>
      )}
      <div className="mt-4 overflow-x-auto rounded-lg border border-cyan-100 bg-white">
        <table className="w-full text-sm" data-testid="hk-invries-table">
          <thead className="bg-cyan-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Datum</th>
              <th className="px-3 py-2">Ham/kaas</th>
              <th className="px-3 py-2">Nieuwe trace</th>
              <th className="px-3 py-2">Brontrace</th>
              <th className="px-3 py-2">Locatie</th>
              <th className="px-3 py-2 text-right">Ingevroren</th>
              <th className="px-3 py-2 text-right">Verbruikt</th>
              <th className="px-3 py-2 text-right">Rest</th>
              {canEdit && <th className="px-3 py-2">Telling/correctie</th>}
            </tr>
          </thead>
          <tbody>
            {stats.length === 0 ? (
              <tr data-testid="hk-invries-empty">
                <td className="px-3 py-6 text-center text-slate-400" colSpan={canEdit ? 9 : 8}>
                  Nog geen gesneden/invriespartijen geregistreerd.
                </td>
              </tr>
            ) : stats.map((batch) => {
              const used = number(batch.kg) + number(batch.correctie) - batch.rest;
              return (
                <tr key={batch.id} className="border-t border-slate-100" data-testid="hk-invries-row">
                  <td className="px-3 py-2">{batch.datum || "—"}</td>
                  <td className="px-3 py-2 font-medium">{batch.naam}</td>
                  <td className="px-3 py-2 font-mono text-xs">{batch.tracecode}</td>
                  <td className="px-3 py-2 font-mono text-xs">{batch.bronTracecode || "—"}</td>
                  <td className="px-3 py-2">{batch.locatie || "—"}</td>
                  <td className="px-3 py-2 text-right">{kgf(batch.kg)}</td>
                  <td className="px-3 py-2 text-right">{kgf(Math.max(used, 0))}</td>
                  <td className="px-3 py-2 text-right font-semibold">{kgf(Math.max(batch.rest, 0))}</td>
                  {canEdit && (
                    <td className="px-3 py-2">
                      <div className="flex min-w-[190px] items-center gap-2">
                        <input
                          type="number"
                          step="any"
                          data-testid="hk-invries-count"
                          className="w-20 rounded border border-slate-300 px-2 py-1 text-sm"
                          value={counts[batch.id] ?? ""}
                          placeholder="tel…"
                          onChange={(event) => setCounts({
                            ...counts,
                            [batch.id]: event.target.value,
                          })}
                        />
                        <button
                          data-testid="hk-invries-adopt"
                          disabled={counts[batch.id] === undefined || counts[batch.id] === ""}
                          onClick={() => {
                            onAdoptCount?.(batch.id, counts[batch.id]);
                            setCounts({ ...counts, [batch.id]: "" });
                          }}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-cyan-800
                            disabled:text-slate-400"
                        >
                          <Check className="h-3.5 w-3.5" /> Neem over
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
    </details>
  );
}